import dropdownMixmin from '../mixins';
import VirtualList from 'vue-virtual-scroll-list';

import CommonButton from '@design/components/button/Button';
import Checkbox from '@components/Common/Checkbox.js';
import { isEllipsisActive } from '@util';

import * as S from './styles';
import * as DS from '../defaultDropdown/styles';

export default {
    name: 'CheckboxDropdown',
    mixins: [dropdownMixmin],
    props: {
        selectedOption: {
            type: Array,
            default: [],
        },
        keywordVisibility: {
            type: Object,
            default() {
                return { display: false, suffix: '' };
            },
        },
        hasSelectAll: {
            type: Boolean,
            default: false,
        },
        limitedOptions: {
            type: Object,
            default() {
                return {
                    cnt: undefined,
                    callback: () => {},
                };
            },
        },
        useVirtualScroll: Boolean,
    },
    data() {
        return {
            currentSelectedOption: this.selectedOption,
            activeTooltipKey: null,
            activeTooltipText: '',
            anchorRect: { top: 0, left: 0, width: 0, height: 0 },
        };
    },
    watch: {
        selectedOption(options) {
            this.currentSelectedOption = options;
        },
    },
    computed: {
        virtualListSources() {
            const { filteredOptions } = this;

            return filteredOptions.map((option, index) => ({
                index,
                key: option + index,
                option: option,
            }));
        },
    },
    methods: {
        updateSelectedItem(option) {
            // Block disabled options from being selected
            if (option?.disabled) {
                return;
            }

            const {
                currentSelectedOption,
                hasConfirmButton,
                limitedOptions: { cnt, callback },
            } = this;

            const currentOptions = R.ifElse(
                R.includes(option),
                R.filter(R.compose(R.not, R.equals(option))),
                R.concat([option])
            )(currentSelectedOption);

            if (cnt && cnt < currentOptions.length) {
                callback();
                return;
            } else {
                this.currentSelectedOption = currentOptions;

                if (!hasConfirmButton) {
                    this.$emit('confirm', this.currentSelectedOption);
                }
            }
        },
        onEllipsisOptionEnter(key, tooltip, ev) {
            const el = ev?.currentTarget;
            const textEl = el?.querySelector?.('p.text');
            if (el && textEl && isEllipsisActive(textEl)) {
                const wrapperEl = this.$refs.optionListWrapper?.$el || this.$refs.optionListWrapper;
                if (!wrapperEl) return;
                const itemRect = el.getBoundingClientRect();
                const wrapperRect = wrapperEl.getBoundingClientRect();
                this.anchorRect = {
                    top: itemRect.top - wrapperRect.top,
                    left: itemRect.left - wrapperRect.left,
                    width: itemRect.width,
                    height: itemRect.height,
                };
                this.activeTooltipText = tooltip;
                // Defer visibility toggle by one frame so the anchor span's
                // inline style is painted before antd mounts and measures it.
                if (this._tooltipShowRaf) cancelAnimationFrame(this._tooltipShowRaf);
                this._tooltipShowRaf = requestAnimationFrame(() => {
                    this.activeTooltipKey = key;
                    this._tooltipShowRaf = null;
                });
            } else if (this.activeTooltipKey !== null) {
                // X1 else-clear: clear stale anchor when hovering a non-truncated item
                // catches mouseenter-B-before-mouseleave-A race that causes tooltip jump
                if (this._tooltipShowRaf) {
                    cancelAnimationFrame(this._tooltipShowRaf);
                    this._tooltipShowRaf = null;
                }
                this.activeTooltipKey = null;
            }
        },
        onOptionListScroll() {
            // X1 scroll-clear: prevent virtual-scroll recycle and offscreen anchor persistence (C2·C3)
            if (this._tooltipShowRaf) {
                cancelAnimationFrame(this._tooltipShowRaf);
                this._tooltipShowRaf = null;
            }
            if (this.activeTooltipKey !== null) this.activeTooltipKey = null;
        },
        renderVirtual() {
            const {
                virtualListSources,
                wrapTooltip,
                renderOption,
                tooltipGenerator,
                tooltipOnEllipsis,
                width,
                currentSelectedOption,
                updateSelectedItem,
                blockedOptions,
                onEllipsisOptionEnter,
            } = this;

            return (
                <VirtualList
                    class="dropdown-virtual-scroller"
                    data-key="key"
                    data-sources={virtualListSources}
                    data-component={ItemElement}
                    keeps={20}
                    extra-props={{
                        wrapTooltip,
                        renderOption,
                        tooltipGenerator,
                        tooltipOnEllipsis,
                        width,
                        currentSelectedOption,
                        updateSelectedItem,
                        blockedOptions,
                        onEllipsisOptionEnter,
                    }}
                />
            );
        },
        renderOptionList() {
            const {
                width,
                height,
                filteredOptions,
                currentSelectedOption,
                renderEmptyOption,
                updateSelectedItem,
                renderOption,
                hasSearchBar,
                keywordVisibility: { display, suffix },
                keyword,
                tooltipOnEllipsis,
                tooltipGenerator,
                useVirtualScroll,
                renderVirtual,
                activeTooltipKey,
                activeTooltipText,
                anchorRect,
            } = this;

            const showKeywordUI = display && keyword;

            return (
                <DS.OptionListWrapper
                    ref="optionListWrapper"
                    width={width}
                    height={height}
                    hasSearchBar={hasSearchBar}
                    displayKeyword={display}
                    useVirtualScroll={useVirtualScroll}
                    onMouseleave={() => {
                        if (this._tooltipShowRaf) {
                            cancelAnimationFrame(this._tooltipShowRaf);
                            this._tooltipShowRaf = null;
                        }
                        this.activeTooltipKey = null;
                    }}
                >
                    {showKeywordUI && (
                        <S.KeywordActionWrapper
                            onClick={() =>
                                this.$emit('actionWithKeyword', keyword)
                            }
                            hasResults={Boolean(filteredOptions.length)}
                            maxWidth={width}
                        >
                            <span class="keyword">"{keyword}"</span>
                            {suffix && <span class="suffix">{suffix}</span>}
                        </S.KeywordActionWrapper>
                    )}
                    <ul>
                        {useVirtualScroll
                            ? renderVirtual()
                            : filteredOptions.length
                            ? filteredOptions.map((option, index) => {
                                  const tooltip = tooltipGenerator(option);
                                  const text = renderOption(option);
                                  const optionKey = option + index;

                                  if (tooltipOnEllipsis) {
                                      return (
                                          <S.OptionItem
                                              maxWidth={width}
                                              isBlocked={option?.disabled}
                                              class="item"
                                              onMouseenter={(e) => this.onEllipsisOptionEnter(optionKey, tooltip, e)}
                                              onClick={(e) => { e.stopPropagation(); updateSelectedItem(option); }}
                                          >
                                              <Checkbox
                                                  value={R.includes(
                                                      option,
                                                      currentSelectedOption
                                                  )}
                                              >
                                                  <p class="text">{text}</p>
                                              </Checkbox>
                                          </S.OptionItem>
                                      );
                                  }
                                  return this.wrapTooltip({
                                      visible: Boolean(tooltip),
                                      title: tooltip,
                                      content: (
                                          <S.OptionItem
                                              maxWidth={width}
                                              isBlocked={option?.disabled}
                                              class="item"
                                              onClick={(e) => {
                                                  e.stopPropagation();
                                                  updateSelectedItem(option);
                                              }}
                                          >
                                              <Checkbox
                                                  value={R.includes(
                                                      option,
                                                      currentSelectedOption
                                                  )}
                                              >
                                                  <p class="text">{text}</p>
                                              </Checkbox>
                                          </S.OptionItem>
                                      ),
                                  });
                              })
                            : showKeywordUI
                            ? null
                            : renderEmptyOption()}
                    </ul>
                    {tooltipOnEllipsis && (
                        <a-tooltip
                            key={activeTooltipKey || 'tooltip-hidden'}
                            visible={activeTooltipKey !== null}
                            title={activeTooltipText}
                            getPopupContainer={() => document.body}
                            placement="top"
                            destroyTooltipOnHide={true}
                            // popup sits outside the wrapper: overlapping the cursor would
                            // fire the wrapper mouseleave-clear and loop show/hide
                            overlayStyle={{ pointerEvents: 'none' }}
                        >
                            <span
                                ref="tooltipAnchor"
                                style={{
                                    position: 'absolute',
                                    pointerEvents: 'none',
                                    opacity: 0,
                                    top: `${anchorRect.top}px`,
                                    left: `${anchorRect.left}px`,
                                    width: `${anchorRect.width}px`,
                                    height: `${anchorRect.height}px`,
                                }}
                            />
                        </a-tooltip>
                    )}
                </DS.OptionListWrapper>
            );
        },
        renderButtons() {
            const {
                currentSelectedOption,
                hasConfirmButton,
                filteredOptions,
                hasSelectAll,
            } = this;

            return hasConfirmButton ? (
                <S.ButtonWrapper>
                    {currentSelectedOption.length > 0 ? (
                        <CommonButton
                            type="white"
                            onClick={() => (this.currentSelectedOption = [])}
                        >
                            {this.$t('COMMON.unselect_all')}
                        </CommonButton>
                    ) : hasSelectAll ? (
                        <CommonButton
                            type="white"
                            onClick={() => {
                                const selectableOptions = filteredOptions.filter(
                                    option => !option?.disabled
                                );
                                this.currentSelectedOption = [...selectableOptions];
                            }}
                        >
                            {this.$t('COMMON.select_all')}
                        </CommonButton>
                    ) : null}
                    <CommonButton
                        type="primary"
                        onClick={() => this.submit({ isCheckBox: true })}
                    >
                        {this.$t('COMMON.apply')}
                    </CommonButton>
                </S.ButtonWrapper>
            ) : null;
        },
    },
    mounted() {
        this.$el.addEventListener('scroll', this.onOptionListScroll, true);
    },
    beforeDestroy() {
        this.$el.removeEventListener('scroll', this.onOptionListScroll, true);
        if (this._tooltipShowRaf) cancelAnimationFrame(this._tooltipShowRaf);
    },
    render() {
        const {
            renderTitle,
            renderSearchInput,
            renderOptionList,
            renderButtons,
            width,
            isVisible,
        } = this;

        return (
            <DS.ComponentWrapper
                width={width}
                isVisible={isVisible}
                id="commonDropboxWrapper"
                ref="dropboxWrapper"
            >
                <div>{this.$slots.title || renderTitle()}</div>
                <div>{renderSearchInput()}</div>
                <div>{renderOptionList()}</div>
                <div>{renderButtons()}</div>
            </DS.ComponentWrapper>
        );
    },
};

const ItemElement = {
    functional: true,
    props: {
        source: {
            type: Object,
            default() {
                return {};
            },
        },
        wrapTooltip: {
            type: Function,
            default: () => {},
        },
        lineClampOption: Object,
        renderOption: Function,
        tooltipGenerator: Function,
        tooltipOnEllipsis: Boolean,
        width: Number,
        currentSelectedOption: Array,
        updateSelectedItem: Function,
        blockedOptions: {
            type: Array,
            default() {
                return [];
            },
        },
        onEllipsisOptionEnter: { type: Function, default: () => {} },
    },
    render(h, { props }) {
        const {
            source,
            wrapTooltip,
            renderOption,
            tooltipGenerator,
            tooltipOnEllipsis,
            updateSelectedItem,
            width,
            currentSelectedOption,
            blockedOptions,
            onEllipsisOptionEnter,
        } = props;
        const { index, key, option } = source;

        // Check BOTH blocking patterns
        const isBlocked =
            blockedOptions.indexOf(option) > -1 ||  // blockedOptions array
            option?.disabled;                        // disabled flag

        const tooltip = tooltipGenerator(option);
        const text = renderOption(option);

        if (tooltipOnEllipsis) {
            return (
                <S.OptionItem
                    maxWidth={width}
                    isBlocked={isBlocked}
                    class="option-item"
                    data-index={index}
                    key={key}
                    onMouseenter={(e) => onEllipsisOptionEnter(source.key, tooltip, e)}
                    onClick={(e) => { e.stopPropagation(); updateSelectedItem(option); }}
                >
                    <Checkbox value={R.includes(option, currentSelectedOption)}>
                        <p class="text">{text}</p>
                    </Checkbox>
                </S.OptionItem>
            );
        }
        return wrapTooltip({
            visible: Boolean(tooltip),
            title: tooltip,
            content: (
                <S.OptionItem
                    maxWidth={width}
                    isBlocked={isBlocked}
                    class="option-item"
                    data-index={index}
                    key={key}
                    onClick={(e) => {
                        e.stopPropagation();
                        updateSelectedItem(option);
                    }}
                >
                    <Checkbox value={R.includes(option, currentSelectedOption)}>
                        <p class="text">{text}</p>
                    </Checkbox>
                </S.OptionItem>
            ),
        });
    },
};
